import { createLogger } from '../../Utils/Logger';
/**
 * 脏标记类型
 */
export var DirtyFlag;
(function (DirtyFlag) {
    /** 组件数据已修改 */
    DirtyFlag[DirtyFlag["COMPONENT_MODIFIED"] = 1] = "COMPONENT_MODIFIED";
    /** 组件已添加 */
    DirtyFlag[DirtyFlag["COMPONENT_ADDED"] = 2] = "COMPONENT_ADDED";
    /** 组件已移除 */
    DirtyFlag[DirtyFlag["COMPONENT_REMOVED"] = 4] = "COMPONENT_REMOVED";
    /** 实体位置已改变 */
    DirtyFlag[DirtyFlag["TRANSFORM_CHANGED"] = 8] = "TRANSFORM_CHANGED";
    /** 实体状态已改变 */
    DirtyFlag[DirtyFlag["STATE_CHANGED"] = 16] = "STATE_CHANGED";
    /** 自定义标记1 */
    DirtyFlag[DirtyFlag["CUSTOM_1"] = 256] = "CUSTOM_1";
    /** 自定义标记2 */
    DirtyFlag[DirtyFlag["CUSTOM_2"] = 512] = "CUSTOM_2";
    /** 自定义标记3 */
    DirtyFlag[DirtyFlag["CUSTOM_3"] = 1024] = "CUSTOM_3";
    /** 所有标记 */
    DirtyFlag[DirtyFlag["ALL"] = 4294967295] = "ALL";
})(DirtyFlag || (DirtyFlag = {}));
/**
 * 脏标记追踪系统
 *
 * 提供高效的组件和实体变更追踪，避免不必要的计算和更新。
 * 支持细粒度的脏标记和批量处理机制。
 *
 * @example
 * ```typescript
 * const dirtySystem = new DirtyTrackingSystem();
 *
 * // 标记实体的位置组件已修改
 * dirtySystem.markDirty(entity, DirtyFlag.TRANSFORM_CHANGED, [PositionComponent]);
 *
 * // 监听位置变化
 * dirtySystem.addListener({
 *     flags: DirtyFlag.TRANSFORM_CHANGED,
 *     callback: (data) => {
 *         logger.debug('Entity position changed:', data.entity.name);
 *     }
 * });
 *
 * // 处理所有脏标记
 * dirtySystem.processDirtyEntities();
 * ```
 */
export class DirtyTrackingSystem {
    constructor() {
        /** 脏实体映射表 */
        this._dirtyEntities = new Map();
        /** 脏标记监听器 */
        this._listeners = [];
        /** 性能统计 */
        this._stats = {
            totalMarkings: 0,
            totalCleanups: 0,
            frameCount: 0,
            totalDirtyPerFrame: 0
        };
        /** 当前帧编号 */
        this._currentFrame = 0;
        this._batchSize = 100;
        this._maxProcessingTime = 16;
        /** 延迟处理队列 */
        this._processingQueue = [];
        this._isProcessing = false;
    }
    /**
     * 标记实体为脏状态
     *
     * @param entity 要标记的实体
     * @param flags 脏标记位
     * @param modifiedComponents 修改的组件类型列表
     */
    markDirty(entity, flags, modifiedComponents = []) {
        this._stats.totalMarkings++;
        let dirtyData = this._dirtyEntities.get(entity);
        if (!dirtyData) {
            dirtyData = {
                entity,
                flags: 0,
                modifiedComponents: new Set(),
                timestamp: performance.now(),
                frameNumber: this._currentFrame
            };
            this._dirtyEntities.set(entity, dirtyData);
        }
        dirtyData.flags |= flags;
        dirtyData.timestamp = performance.now();
        dirtyData.frameNumber = this._currentFrame;
        for (const componentType of modifiedComponents) {
            dirtyData.modifiedComponents.add(componentType);
        }
        this.notifyListeners(dirtyData, flags);
    }
    /**
     * 检查实体是否有指定的脏标记
     *
     * @param entity 要检查的实体
     * @param flags 要检查的标记位
     * @returns 是否有指定的脏标记
     */
    isDirty(entity, flags = DirtyFlag.ALL) {
        const dirtyData = this._dirtyEntities.get(entity);
        return dirtyData ? (dirtyData.flags & flags) !== 0 : false;
    }
    /**
     * 清除实体的脏标记
     *
     * @param entity 要清除的实体
     * @param flags 要清除的标记位，默认清除所有
     */
    clearDirty(entity, flags = DirtyFlag.ALL) {
        const dirtyData = this._dirtyEntities.get(entity);
        if (!dirtyData)
            return;
        if (flags === DirtyFlag.ALL) {
            this._dirtyEntities.delete(entity);
        }
        else {
            dirtyData.flags &= ~flags;
            if (dirtyData.flags === 0) {
                this._dirtyEntities.delete(entity);
            }
        }
        this._stats.totalCleanups++;
    }
    /**
     * 获取所有脏实体
     *
     * @param flags 过滤标记位，只返回包含指定标记的实体
     * @returns 脏实体数据数组
     */
    getDirtyEntities(flags = DirtyFlag.ALL) {
        const result = [];
        for (const dirtyData of this._dirtyEntities.values()) {
            if ((dirtyData.flags & flags) !== 0) {
                result.push(dirtyData);
            }
        }
        return result;
    }
    /**
     * 批量处理脏实体
     *
     * 使用时间分片的方式处理脏实体，避免单帧卡顿
     */
    processDirtyEntities() {
        if (this._isProcessing)
            return;
        this._isProcessing = true;
        const startTime = performance.now();
        if (this._processingQueue.length === 0) {
            this._processingQueue.push(...this._dirtyEntities.values());
        }
        let processed = 0;
        while (this._processingQueue.length > 0 && processed < this._batchSize) {
            const elapsed = performance.now() - startTime;
            if (elapsed > this._maxProcessingTime) {
                break;
            }
            const dirtyData = this._processingQueue.shift();
            this.processEntity(dirtyData);
            processed++;
        }
        if (this._processingQueue.length === 0) {
            this._isProcessing = false;
            this.onFrameEnd();
        }
    }
    /**
     * 添加脏标记监听器
     *
     * @param listener 监听器配置
     */
    addListener(listener) {
        this._listeners.push(listener);
        this._listeners.sort((a, b) => (a.priority || 100) - (b.priority || 100));
    }
    /**
     * 移除脏标记监听器
     *
     * @param callback 要移除的回调函数
     */
    removeListener(callback) {
        const index = this._listeners.findIndex(l => l.callback === callback);
        if (index !== -1) {
            this._listeners.splice(index, 1);
        }
    }
    /**
     * 开始新的帧
     */
    beginFrame() {
        this._currentFrame++;
    }
    /**
     * 结束当前帧
     */
    endFrame() {
        if (!this._isProcessing) {
            this.processDirtyEntities();
        }
    }
    /**
     * 获取统计信息
     */
    getStats() {
        return {
            dirtyEntityCount: this._dirtyEntities.size,
            totalMarkings: this._stats.totalMarkings,
            totalCleanups: this._stats.totalCleanups,
            listenerCount: this._listeners.length,
            avgDirtyPerFrame: this._stats.frameCount > 0 ?
                this._stats.totalDirtyPerFrame / this._stats.frameCount : 0,
            estimatedMemoryUsage: this.estimateMemoryUsage()
        };
    }
    /**
     * 清空所有脏标记和统计信息
     */
    clear() {
        this._dirtyEntities.clear();
        this._processingQueue.length = 0;
        this._isProcessing = false;
        this._stats = {
            totalMarkings: 0,
            totalCleanups: 0,
            frameCount: 0,
            totalDirtyPerFrame: 0
        };
    }
    /**
     * 配置批量处理参数
     *
     * @param batchSize 每次处理的最大实体数量
     * @param maxProcessingTime 每帧最大处理时间（毫秒）
     */
    configureBatchProcessing(batchSize, maxProcessingTime) {
        this._batchSize = batchSize;
        this._maxProcessingTime = maxProcessingTime;
    }
    /**
     * 处理单个脏实体
     */
    processEntity(dirtyData) {
        for (const listener of this._listeners) {
            if ((dirtyData.flags & listener.flags) !== 0) {
                try {
                    listener.callback(dirtyData);
                }
                catch (error) {
                    DirtyTrackingSystem._logger.error('脏数据监听器错误:', error);
                }
            }
        }
        this.clearDirty(dirtyData.entity);
    }
    /**
     * 通知监听器
     */
    notifyListeners(dirtyData, newFlags) {
        for (const listener of this._listeners) {
            if ((newFlags & listener.flags) !== 0) {
                try {
                    listener.callback(dirtyData);
                }
                catch (error) {
                    DirtyTrackingSystem._logger.error('脏数据监听器通知错误:', error);
                }
            }
        }
    }
    /**
     * 帧结束时的统计更新
     */
    onFrameEnd() {
        this._stats.frameCount++;
        this._stats.totalDirtyPerFrame += this._dirtyEntities.size;
    }
    /**
     * 估算内存使用量
     */
    estimateMemoryUsage() {
        let usage = 0;
        usage += this._dirtyEntities.size * 100;
        usage += this._listeners.length * 50;
        usage += this._processingQueue.length * 8;
        return usage;
    }
}
DirtyTrackingSystem._logger = createLogger('DirtyTrackingSystem');
//# sourceMappingURL=DirtyTrackingSystem.js.map